// client/src/features/health/search.ts — SCR-HS-001's and SCR-HS-002's URL state · HS-003
// (04 §9.2.3).
//
// §9.2.3 is normative for every LIST screen: sort and filters live in the URL as Zod-validated
// search params, so a sorted heat list is shareable, survives a refresh, and Back is an undo.
// `features/equipment/search.ts` is the shape this copies.
//
// NONE OF THESE PARAMS REACHES THE SERVER. `GET /api/v1/health-scores` takes no query string at
// all: it answers the whole portfolio in one payload, worst score first. So the sort and the
// band filter are applied to the rows in hand — which is the honest place for them, because the
// list is bounded by the number of properties an organization owns and a second round trip to
// reorder ten rows would be a request whose answer the browser already has.
//
// SCR-HS-002's `component` IS AN OVERLAY PARAM, NOT A FILTER. §9.2.3's canonical
// `?explain=$obligationId` is the pattern: the penalty drawer is addressable, so „de ce are
// apartamentul de pe Aviatorilor doar 7 din 15 la mentenanță" is a link, a refresh keeps the
// drawer open and Back closes it. Closing strips only that param.

import { z } from 'zod';

import { HEALTH_SCORE_BANDS } from './enums';

/**
 * What the heat list can be ordered by. `score` is the default and the reason the screen
 * exists — 04 §8.13 asks for „heat list sortable by score" and nothing else — but a portfolio
 * of thirty flats is also read alphabetically, and `delta` answers „what got worse this week",
 * which is the question the trend arrow poses and could not otherwise be sorted on.
 */
export const HEALTH_SORT_KEYS = ['score', 'name', 'delta'] as const;

export type HealthSortKey = (typeof HEALTH_SORT_KEYS)[number];

export const HEALTH_SORT_DIRECTIONS = ['asc', 'desc'] as const;

export type HealthSortDirection = (typeof HEALTH_SORT_DIRECTIONS)[number];

/**
 * SCR-HS-001. The defaults are the server's own order — ascending score, worst first — so the
 * unsorted URL and the payload agree and the first paint does not reorder itself.
 */
export const healthSearchSchema = z.object({
  sort: z.enum(HEALTH_SORT_KEYS).default('score'),
  dir: z.enum(HEALTH_SORT_DIRECTIONS).default('asc'),
  /** One band, or all of them. A red-only heat list is the list a landlord acts from. */
  band: z.enum(HEALTH_SCORE_BANDS).optional(),
});

export type HealthSearch = z.infer<typeof healthSearchSchema>;

/**
 * SCR-HS-002. `component` names the open drawer; `trendDays` is the sparkline's window and is
 * the ONE param that does travel — it is `GET /health-scores/{id}`'s own query parameter.
 *
 * The bound spelled here is the server's `MaxTrendDays`. A URL asking for more does not travel:
 * Zod refuses it and TanStack raises a `SearchParamError`, so the route renders its error rather
 * than the screen. That is NOT a silent fallback to the default, and the suite asserts the loud
 * behaviour rather than a kinder one nobody wrote — every §9.2.3 list screen in this product
 * behaves the same way (`equipmentSearchSchema.pageSize` past 200 throws identically), because
 * no search schema here carries a `.catch`. Making this one param forgiving alone would be a
 * second mechanism for a decision the whole product has already made one way.
 */
export const propertyHealthSearchSchema = z.object({
  component: z.string().min(1).max(64).optional(),
  trendDays: z.coerce.number().int().min(1).max(365).optional(),
});

export type PropertyHealthSearch = z.infer<typeof propertyHealthSearchSchema>;

/** Whether the heat list is showing everything it was given — the empty state needs to know. */
export function hasActiveFilters(search: HealthSearch): boolean {
  return search.band !== undefined;
}
